import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { Shield, User, Menu, X, LogOut, ChevronDown, LayoutDashboard, FileText } from 'lucide-react';
import { API_BASE_URL } from './config';
import { ThemeProvider } from './components/ThemeProvider';
import ThemeToggle from './components/ThemeToggle';

type ProfileUser = {
  id: number;
  username: string;
  name: string;
  email: string;
};

type MessageTone = 'success' | 'error' | 'info';

type MessageModalState = {
  isOpen: boolean;
  title: string;
  message: string;
  tone: MessageTone;
};

const MessageModal = ({
  isOpen,
  title,
  message,
  tone = 'info',
  onClose
}: {
  isOpen: boolean;
  title: string;
  message: string;
  tone?: MessageTone;
  onClose: () => void;
}) => {
  if (!isOpen) {
    return null;
  }

  const toneStyles: Record<MessageTone, { badge: string; button: string }> = {
    success: {
      badge: 'bg-green-100 text-green-700',
      button: 'bg-green-600 hover:bg-green-700'
    },
    error: {
      badge: 'bg-red-100 text-red-700',
      button: 'bg-red-600 hover:bg-red-700'
    },
    info: {
      badge: 'bg-blue-100 text-blue-700',
      button: 'bg-blue-600 hover:bg-blue-700'
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-gray-600 bg-opacity-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-md border bg-white p-5 shadow-lg">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <span className={`inline-flex rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide ${toneStyles[tone].badge}`}>
              {tone}
            </span>
            <h3 className="mt-2 text-lg font-bold text-gray-900">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 cursor-pointer"
            aria-label="Fechar mensagem"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-gray-700">{message}</p>
        <div className="mt-5 flex justify-end">
          <button
            onClick={onClose}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white cursor-pointer ${toneStyles[tone].button}`}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const getApiErrorMessage = async (response: Response, fallback: string) => {
  try {
    const payload = await response.json();
    if (payload && typeof payload.error === 'string' && payload.error.trim().length > 0) {
      return payload.error;
    }
  } catch (error) {
    console.error('Failed to parse API error response:', error);
  }

  return fallback;
};

// App Password Component
const AppPassword = ({ onAppLogin }: { onAppLogin: () => void }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dbStatus, setDbStatus] = useState<'checking' | 'ok' | 'error'>('checking');
  const [dbMessage, setDbMessage] = useState('Verificando ligacao com base de dados...');

  // Check database connection on component mount
  useEffect(() => {
    const checkDatabaseConnection = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await fetch(`${API_BASE_URL}/api/health/check-db`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            setDbStatus('ok');
            setDbMessage('Base de dados conectada');
          } else {
            setDbStatus('error');
            setDbMessage('Falha na ligacao com a base de dados');
          }
        } else {
          setDbStatus('ok');
          setDbMessage('Pronto para conectar');
        }
      } catch (error) {
        setDbStatus('error');
        setDbMessage('Erro ao conectar com a base de dados');
        console.error('Database check error:', error);
      }
    };

    checkDatabaseConnection();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Simple app password check
    if (password === 'familiacruz') {
      onAppLogin();
    } else {
      setError('Senha do app incorreta');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Shield className="mx-auto h-12 w-12 text-blue-600 dark:text-blue-400" />
        <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Portal Seguros</h2>
        <p className="text-gray-600 dark:text-gray-400">Digite a senha do aplicativo</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Database Status */}
          <div className={`mb-6 p-3 rounded-md text-sm ${
            dbStatus === 'ok' ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
            dbStatus === 'checking' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
            'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300'
          }`}>
            <p className="font-medium">{dbMessage}</p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Senha do App
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {error && (
              <div className="text-red-600 dark:text-red-400 text-sm">{error}</div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// User Selection Component
const UserSelection = ({
  users,
  loading,
  error,
  onUserSelect
}: {
  users: ProfileUser[];
  loading: boolean;
  error: string;
  onUserSelect: (user: ProfileUser) => void;
}) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Shield className="mx-auto h-12 w-12 text-blue-600 dark:text-blue-400" />
        <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Selecione seu Perfil</h2>
        <p className="text-gray-600 dark:text-gray-400">Escolha qual cliente voce e</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {loading && (
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">A carregar perfis...</p>
          )}
          {error && (
            <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="space-y-4">
            {!loading && users.length === 0 && (
              <p className="text-sm text-gray-600 dark:text-gray-300">Nenhum perfil disponivel.</p>
            )}
            {users.map((user) => (
              <button
                key={user.id}
                onClick={() => onUserSelect(user)}
                className="w-full text-left p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
              >
                <div className="flex items-center">
                  <User className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">{user.name}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ user }: { user: any }) => {
  const [showFamilyView, setShowFamilyView] = useState(false);
  const [messageModal, setMessageModal] = useState<MessageModalState>({
    isOpen: false,
    title: '',
    message: '',
    tone: 'info'
  });
  const [stats, setStats] = useState({
    totalClaims: 0,
    pendingClaims: 0,
    approvedClaims: 0,
    closedClaims: 0,
    totalSpent: 0,
    totalReimbursed: 0,
    pendingReimbursement: 0,
    goalAmount: 2000,
    goalProgress: 0,
    goalProgressWithOpen: 0
  });
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    claim_date: '',
    compensation_percentage: '90',
    claim_amount: ''
  });

  const openMessageModal = (title: string, message: string, tone: MessageTone = 'info') => {
    setMessageModal({
      isOpen: true,
      title,
      message,
      tone
    });
  };

  const fetchDashboardStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = showFamilyView ? `${API_BASE_URL}/api/claims/family/all` : `${API_BASE_URL}/api/claims`;
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });

      if (response.ok) {
        const claims = await response.json();
        const pendingClaims = claims.filter((c: any) => c.status === 'pending');
        const approvedClaims = claims.filter((c: any) => c.status === 'approved');
        const closedClaims = claims.filter((c: any) => c.status === 'closed');
        const openClaims = claims.filter((c: any) => c.status !== 'closed');
        
        // Valor Total Gasto: sum of ALL processes no matter status
        const totalSpent = claims.reduce((sum: number, claim: any) => sum + parseFloat(claim.claim_amount || 0), 0);
        
        // Valor Total Reembolsado: actual reimbursed amount for closed claims
        const totalReimbursed = closedClaims.reduce((sum: number, claim: any) => sum + parseFloat(claim.actual_reimbursed_amount || claim.reimbursement_amount || 0), 0);
        
        // Total a Reembolsar: reimbursement amount for all open claims (pending + approved)
        const pendingReimbursement = openClaims.reduce((sum: number, claim: any) => sum + parseFloat(claim.reimbursement_amount || 0), 0);
        
        const goalAmount = 2000;
        const goalProgress = Math.min((totalReimbursed / goalAmount) * 100, 100);
        const goalProgressWithOpen = Math.min(((totalReimbursed + pendingReimbursement) / goalAmount) * 100, 100);

        setStats({
          totalClaims: claims.length,
          pendingClaims: pendingClaims.length,
          approvedClaims: approvedClaims.length,
          closedClaims: closedClaims.length,
          totalSpent,
          totalReimbursed,
          pendingReimbursement,
          goalAmount,
          goalProgress,
          goalProgressWithOpen
        });
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  }, [user, showFamilyView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/claims`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store',
        body: JSON.stringify({
          ...formData,
          claim_amount: parseFloat(formData.claim_amount),
          compensation_percentage: parseFloat(formData.compensation_percentage),
          customer_id: user.id
        }),
      });

      if (response.ok) {
        setShowModal(false);
        setFormData({ description: '', claim_date: '', compensation_percentage: '90', claim_amount: '' });
        fetchDashboardStats();
      } else {
        openMessageModal('Erro', 'Falha ao submeter processo.', 'error');
      }
    } catch (error) {
      console.error('Error submitting claim:', error);
      openMessageModal('Erro', 'Falha ao submeter processo. Tente novamente.', 'error');
    }
  };

  const formatCurrency = (amount: number) => {
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
    return `${numAmount.toFixed(2).replace('.', ',')} €`;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Bem-vindo(a), {user.name}!</h1>
            <p className="text-gray-600">
              {showFamilyView ? 'Aqui esta o resumo da familia' : 'Aqui esta um resumo do seu portfolio de seguros'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {user.username === 'luis' && (
              <button
                onClick={() => setShowFamilyView(!showFamilyView)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  showFamilyView ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
                }`}
              >
                {showFamilyView ? 'Ver Meus' : 'Ver Familia'}
              </button>
            )}
            {!showFamilyView && (
              <button
                onClick={() => setShowModal(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Novo Processo
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Processos</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalClaims}</p>
            </div>
            <div className="bg-blue-500 p-3 rounded-full">
              <span className="text-white font-bold text-xs">TOTAL</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Processos Pendentes</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.pendingClaims}</p>
            </div>
            <div className="bg-yellow-500 p-3 rounded-full">
              <span className="text-white font-bold text-xs">PEND</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Processos Aprovados</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.approvedClaims}</p>
            </div>
            <div className="bg-blue-500 p-3 rounded-full">
              <span className="text-white font-bold text-xs">APRV</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Processos Fechados</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.closedClaims}</p>
            </div>
            <div className="bg-gray-500 p-3 rounded-full">
              <span className="text-white font-bold text-xs">OK</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Gasto</p>
              <p className="text-3xl font-bold text-red-600">{formatCurrency(stats.totalSpent)}</p>
              <p className="text-xs text-gray-500 mt-1">Todos os processos</p>
            </div>
            <div className="bg-red-500 p-3 rounded-full">
              <span className="text-white font-bold text-xs">OUT</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total a Reembolsar</p>
              <p className="text-3xl font-bold text-yellow-600">{formatCurrency(stats.pendingReimbursement)}</p>
              <p className="text-xs text-gray-500 mt-1">Processos abertos</p>
            </div>
            <div className="bg-yellow-500 p-3 rounded-full">
              <span className="text-white font-bold text-xs">PROC</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Reembolsado</p>
              <p className="text-3xl font-bold text-green-600">{formatCurrency(stats.totalReimbursed)}</p>
              <p className="text-xs text-gray-500 mt-1">Processos fechados</p>
            </div>
            <div className="bg-green-500 p-3 rounded-full">
              <span className="text-white font-bold text-xs">IN</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Limite € {stats.goalAmount}</p>
              <div className="bg-purple-500 p-2 rounded-full">
                <span className="text-white font-bold text-xs">META</span>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${stats.goalProgress}%` }}
              ></div>
            </div>
            <p className="text-sm font-bold text-purple-600 leading-tight">
              {stats.goalProgress.toFixed(1)}% fechados | {stats.goalProgressWithOpen.toFixed(1)}% c/abertos
            </p>
            <p className="text-xs text-gray-600 leading-tight mt-1">
              Com abertos: {formatCurrency(stats.totalReimbursed + stats.pendingReimbursement)}
            </p>
            <p className="text-xs text-gray-600 leading-tight">
              Faltam {formatCurrency(Math.max(0, stats.goalAmount - (stats.totalReimbursed + stats.pendingReimbursement)))} para limite (com fechados)
            </p>
            <p className="text-xs text-gray-600 leading-tight">
              Faltam {formatCurrency(Math.max(0, stats.goalAmount - stats.pendingReimbursement))} para limite (sem fechados)
            </p>
          </div>
        </div>

      </div>

      {/* File Claim Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Novo Processo</h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Descricao</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Data do Processo</label>
                <input
                  type="date"
                  value={formData.claim_date}
                  onChange={(e) => setFormData({...formData, claim_date: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Percentual de Compensacao (%)</label>
                  <select
                    value={formData.compensation_percentage}
                    onChange={(e) => setFormData({...formData, compensation_percentage: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="50">50%</option>
                    <option value="80">80%</option>
                    <option value="90">90%</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Valor Gasto (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.claim_amount}
                    onChange={(e) => setFormData({...formData, claim_amount: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                >
                  Submeter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <MessageModal
        isOpen={messageModal.isOpen}
        title={messageModal.title}
        message={messageModal.message}
        tone={messageModal.tone}
        onClose={() => setMessageModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

// Claims Component
const Claims = ({ user }: { user: any }) => {
  const [claims, setClaims] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [messageModal, setMessageModal] = useState<MessageModalState>({
    isOpen: false,
    title: '',
    message: '',
    tone: 'info'
  });
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [filterPending, setFilterPending] = useState(false);
  const [showFamilyView, setShowFamilyView] = useState(false);
  const [isClosingClaim, setIsClosingClaim] = useState(false);
  const [reimbursementDate, setReimbursementDate] = useState('');
  const [actualReimbursedAmount, setActualReimbursedAmount] = useState('');
  const [approvalDate, setApprovalDate] = useState('');
  const [selectedClaim, setSelectedClaim] = useState<any>(null);
  const [editFormData, setEditFormData] = useState({
    description: '',
    claim_date: '',
    compensation_percentage: '90',
    claim_amount: '',
    status: 'pending'
  });
  const [formData, setFormData] = useState({
    description: '',
    claim_date: '',
    compensation_percentage: '90',
    claim_amount: ''
  });
  const defaultCompensationOptions = ['50', '80', '90'];

  const openMessageModal = (title: string, message: string, tone: MessageTone = 'info') => {
    setMessageModal({
      isOpen: true,
      title,
      message,
      tone
    });
  };

  const fetchClaims = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = showFamilyView ? `${API_BASE_URL}/api/claims/family/all` : `${API_BASE_URL}/api/claims`;
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });

      if (response.ok) {
        const data = await response.json();
        setClaims(data);
      } else {
        console.error('Failed to fetch claims');
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
    }
  }, [user, showFamilyView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/claims`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store',
        body: JSON.stringify({
          ...formData,
          claim_amount: parseFloat(formData.claim_amount),
          compensation_percentage: parseFloat(formData.compensation_percentage),
          customer_id: user.id
        }),
      });

      if (response.ok) {
        setShowModal(false);
        setFormData({ description: '', claim_date: '', compensation_percentage: '90', claim_amount: '' });
        fetchClaims();
      } else {
        openMessageModal('Erro', 'Falha ao submeter processo.', 'error');
      }
    } catch (error) {
      console.error('Error submitting claim:', error);
      openMessageModal('Erro', 'Falha ao submeter processo. Tente novamente.', 'error');
    }
  };

  const handleViewClaim = (claim: any) => {
    setSelectedClaim(claim);
    setIsClosingClaim(false);
    setReimbursementDate('');
    setActualReimbursedAmount('');
    setShowViewModal(true);
  };

  const handleStartCloseClaim = () => {
    if (!selectedClaim) return;
    setIsClosingClaim(true);
    setReimbursementDate(new Date().toISOString().split('T')[0]);
    setActualReimbursedAmount((selectedClaim.reimbursement_amount || 0).toString());
  };

  const handleConfirmCloseClaim = async () => {
    if (!reimbursementDate) {
      openMessageModal('Validacao', 'Por favor, selecione a data de reembolso.', 'info');
      return;
    }

    if (!actualReimbursedAmount) {
      openMessageModal('Validacao', 'Por favor, insira o valor reembolsado.', 'info');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/claims/${selectedClaim.id}/close`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store',
        body: JSON.stringify({
          closed_date: reimbursementDate,
          actual_reimbursed_amount: parseFloat(actualReimbursedAmount)
        })
      });

      if (response.ok) {
        setShowViewModal(false);
        setIsClosingClaim(false);
        setReimbursementDate('');
        setActualReimbursedAmount('');
        fetchClaims();
      } else {
        const errorMessage = await getApiErrorMessage(response, 'Falha ao fechar processo');
        openMessageModal('Erro', errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error closing claim:', error);
      openMessageModal('Erro', 'Falha ao fechar processo. Tente novamente.', 'error');
    }
  };

  const handleEditClaim = (claim: any) => {
    const normalizedCompensation = Number.parseFloat(String(claim.compensation_percentage));
    setSelectedClaim(claim);
    setEditFormData({
      description: claim.description,
      claim_date: claim.claim_date.split('T')[0],
      compensation_percentage: Number.isNaN(normalizedCompensation) ? '90' : normalizedCompensation.toString(),
      claim_amount: claim.claim_amount.toString(),
      status: claim.status || 'pending'
    });
    setShowEditModal(true);
  };

  const handleConfirmEditClaim = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/claims/${selectedClaim.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store',
        body: JSON.stringify({
          description: editFormData.description,
          claim_date: editFormData.claim_date,
          compensation_percentage: parseFloat(editFormData.compensation_percentage),
          claim_amount: parseFloat(editFormData.claim_amount),
          status: editFormData.status
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditFormData({ description: '', claim_date: '', compensation_percentage: '90', claim_amount: '', status: 'pending' });
        fetchClaims();
        openMessageModal('Sucesso', 'Processo atualizado com sucesso.', 'success');
      } else {
        const errorMessage = await getApiErrorMessage(response, 'Falha ao atualizar processo');
        openMessageModal('Erro', errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error updating claim:', error);
      openMessageModal('Erro', 'Falha ao atualizar processo. Tente novamente.', 'error');
    }
  };

  const handleApproval = (claim: any) => {
    setSelectedClaim(claim);
    setApprovalDate(new Date().toISOString().split('T')[0]);
    setShowApprovalModal(true);
  };

  const handleConfirmApproval = async () => {
    if (!approvalDate) {
      openMessageModal('Validacao', 'Por favor, selecione a data de aprovacao.', 'info');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/claims/${selectedClaim.id}/approve`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store',
        body: JSON.stringify({
          approved_date: approvalDate
        })
      });

      if (response.ok) {
        setShowApprovalModal(false);
        setApprovalDate('');
        fetchClaims();
        openMessageModal('Sucesso', 'Processo aprovado com sucesso.', 'success');
      } else {
        const errorMessage = await getApiErrorMessage(response, 'Falha ao aprovar processo');
        openMessageModal('Erro', errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error approving claim:', error);
      openMessageModal('Erro', 'Falha ao aprovar processo. Tente novamente.', 'error');
    }
  };

  const formatCurrency = (amount: number) => {
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
    return `${numAmount.toFixed(2).replace('.', ',')} €`;
  };

  const getClaimStatus = (claim: any) => {
    if (claim.status === 'closed') return 'Fechado';
    if (claim.status === 'approved') return 'Aprovado';
    return 'Pendente';
  };

  const getStatusColor = (claim: any) => {
    if (claim.status === 'closed') return 'bg-green-100 text-green-800';
    if (claim.status === 'approved') return 'bg-blue-100 text-blue-800';
    return 'bg-yellow-100 text-yellow-800';
  };
  const visibleClaims = filterPending
    ? claims.filter((c) => c.status !== 'closed')
    : claims;

  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col gap-4 mb-6 md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Meus Processos</h1>
            <p className="text-sm text-gray-500 mt-1">
              A mostrar {visibleClaims.length} de {claims.length} processos
            </p>
          </div>
          <div className="flex items-center flex-wrap gap-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filterPending}
                onChange={(e) => setFilterPending(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm font-medium text-gray-700">Mostrar apenas abertos</span>
            </label>
            {user.username === 'luis' && (
              <button
                onClick={() => setShowFamilyView(!showFamilyView)}
                className={`px-4 py-2 rounded-md font-medium cursor-pointer ${
                  showFamilyView 
                    ? 'bg-purple-600 text-white hover:bg-purple-700' 
                    : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
                }`}
              >
                {showFamilyView ? 'Ver Meus' : 'Ver Familia'}
              </button>
            )}
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
            >
              Novo Processo
            </button>
          </div>
        </div>

        {visibleClaims.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Nenhum processo encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nº Processo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descricao</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Compensacao</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reembolso</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Etapa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acoes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleClaims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{claim.claim_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{claim.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(claim.claim_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{claim.compensation_percentage}%</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(claim.claim_amount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(
                        claim.status === 'closed'
                          ? (claim.actual_reimbursed_amount || claim.reimbursement_amount || 0)
                          : (claim.reimbursement_amount || 0)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(claim)}`}>
                        {getClaimStatus(claim)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-wrap items-center gap-2">
                        <button 
                          onClick={() => handleViewClaim(claim)}
                          className="inline-block bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer text-xs px-2 py-1 rounded font-medium"
                        >
                          Ver
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* File Claim Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Novo Processo</h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Descricao</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Data do Processo</label>
                <input
                  type="date"
                  value={formData.claim_date}
                  onChange={(e) => setFormData({...formData, claim_date: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Percentual de Compensacao (%)</label>
                  <select
                    value={formData.compensation_percentage}
                    onChange={(e) => setFormData({...formData, compensation_percentage: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="50">50%</option>
                    <option value="80">80%</option>
                    <option value="90">90%</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Valor Gasto (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.claim_amount}
                    onChange={(e) => setFormData({...formData, claim_amount: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                >
                  Submeter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Claim Modal */}
      {showViewModal && selectedClaim && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Detalhes do Processo</h3>
              <button 
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nº do Processo</label>
                  <p className="mt-1 text-lg font-semibold text-gray-900">{selectedClaim.claim_number}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Estado</label>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full mt-1 ${
                    selectedClaim.status === 'closed'
                      ? 'bg-green-100 text-green-800'
                      : selectedClaim.status === 'approved'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedClaim.status === 'closed' ? 'Fechado' : selectedClaim.status === 'approved' ? 'Aprovado' : 'Pendente'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Data do Processo</label>
                  <p className="mt-1 text-gray-900">{new Date(selectedClaim.claim_date).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Descricao</label>
                  <p className="mt-1 text-gray-900">{selectedClaim.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Compensacao</label>
                    <p className="mt-1 text-lg font-semibold text-gray-900">{selectedClaim.compensation_percentage}%</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Valor do Processo</label>
                    <p className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(selectedClaim.claim_amount)}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reembolso Total</label>
                  <div className="mt-1 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(selectedClaim.reimbursement_amount || 0)}
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      {formatCurrency(selectedClaim.claim_amount)} x {selectedClaim.compensation_percentage}% = {formatCurrency(selectedClaim.reimbursement_amount || 0)}
                    </p>
                  </div>
                </div>
                {selectedClaim.approved_date && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Data de Aprovacao</label>
                    <p className="mt-1 text-gray-900">{new Date(selectedClaim.approved_date).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedClaim.closed_date && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Data de Fecho</label>
                    <p className="mt-1 text-gray-900">{new Date(selectedClaim.closed_date).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedClaim.status === 'closed' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Valor Reembolsado</label>
                    <div className="mt-1 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-xl font-bold text-blue-600">
                        {formatCurrency(selectedClaim.actual_reimbursed_amount || selectedClaim.reimbursement_amount || 0)}
                      </p>
                    </div>
                  </div>
                )}
                {isClosingClaim && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Data de Reembolso</label>
                      <input
                        type="date"
                        value={reimbursementDate}
                        onChange={(e) => setReimbursementDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Valor Reembolsado (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={actualReimbursedAmount}
                        onChange={(e) => setActualReimbursedAmount(e.target.value)}
                        placeholder={`Calculado: ${formatCurrency(selectedClaim.reimbursement_amount || 0)}`}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-full"
                        required
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {!isClosingClaim && (
              <div className="mt-6 flex flex-wrap justify-end gap-2 border-t pt-4">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditClaim(selectedClaim);
                  }}
                  className="px-4 py-2 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 cursor-pointer"
                >
                  Editar
                </button>
                {selectedClaim.status === 'pending' && (
                    <>
                      <button
                        onClick={() => {
                          setShowViewModal(false);
                          handleApproval(selectedClaim);
                        }}
                        className="px-4 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 cursor-pointer"
                      >
                        Aprovar
                      </button>
                    </>
                  )}
                {(selectedClaim.status === 'pending' || selectedClaim.status === 'approved') && (
                  <button
                    onClick={handleStartCloseClaim}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 cursor-pointer"
                  >
                    Fechar
                  </button>
                )}
              </div>
            )}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setIsClosingClaim(false);
                  setReimbursementDate('');
                  setActualReimbursedAmount('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                Cancelar
              </button>
              {isClosingClaim ? (
                <button
                  onClick={handleConfirmCloseClaim}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 cursor-pointer"
                >
                  Confirmar Fechamento
                </button>
              ) : (
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Claim Modal */}
      {showEditModal && selectedClaim && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Editar Processo</h3>
              <button 
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Descricao</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Data do Processo</label>
                <input
                  type="date"
                  value={editFormData.claim_date}
                  onChange={(e) => setEditFormData({...editFormData, claim_date: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Percentual de Compensacao (%)</label>
                  <select
                    value={editFormData.compensation_percentage}
                    onChange={(e) => setEditFormData({...editFormData, compensation_percentage: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {!defaultCompensationOptions.includes(editFormData.compensation_percentage) && (
                      <option value={editFormData.compensation_percentage}>
                        {editFormData.compensation_percentage}% (Atual)
                      </option>
                    )}
                    <option value="50">50%</option>
                    <option value="80">80%</option>
                    <option value="90">90%</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Valor Gasto (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editFormData.claim_amount}
                    onChange={(e) => setEditFormData({...editFormData, claim_amount: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Estado</label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="pending">Pendente</option>
                  <option value="approved">Aprovado</option>
                  <option value="closed">Fechado</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmEditClaim}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 cursor-pointer"
                >
                  Guardar Alteracoes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedClaim && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Aprovar Processo</h3>
              <button 
                onClick={() => setShowApprovalModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <span className="font-bold">Processo:</span> {selectedClaim.claim_number}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data de Aprovacao</label>
                <input
                  type="date"
                  value={approvalDate}
                  onChange={(e) => setApprovalDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-full"
                  required
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmApproval}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 cursor-pointer"
              >
                Confirmar Aprovacao
              </button>
            </div>
          </div>
        </div>
      )}

      <MessageModal
        isOpen={messageModal.isOpen}
        title={messageModal.title}
        message={messageModal.message}
        tone={messageModal.tone}
        onClose={() => setMessageModal((prev) => ({ ...prev, isOpen: false }))}
      />

    </div>
  );
};

// Pending Requests Component - REMOVED
// Using filter on Claims page instead

// Main App Component
function App() {
  const [user, setUser] = useState<any>(null);
  const [availableUsers, setAvailableUsers] = useState<ProfileUser[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appAuthenticated, setAppAuthenticated] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      setAppAuthenticated(true);
      setUser(JSON.parse(savedUser));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuOpen && !(event.target as Element).closest('.relative')) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileMenuOpen]);

  const handleAppLogin = () => {
    setAppAuthenticated(true);
  };

  useEffect(() => {
    if (!appAuthenticated) {
      return;
    }

    const fetchProfiles = async () => {
      setProfilesLoading(true);
      setProfilesError('');

      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/profiles`, {
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          cache: 'no-store'
        });

        if (!response.ok) {
          throw new Error('Failed to load profiles');
        }

        const profiles = await response.json();
        setAvailableUsers(profiles);
      } catch (error) {
        console.error('Error loading profiles:', error);
        setProfilesError('Nao foi possivel carregar os perfis.');
      } finally {
        setProfilesLoading(false);
      }
    };

    fetchProfiles();
  }, [appAuthenticated]);

  const handleUserSelect = async (selectedUser: ProfileUser) => {
    try {
      // Login to get real JWT token
      const response = await fetch(`${API_BASE_URL}/api/auth/profile-login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store',
        body: JSON.stringify({
          username: selectedUser.username
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const userWithToken = { ...data.user, token: data.token };
        setUser(userWithToken);
        localStorage.setItem('user', JSON.stringify(userWithToken));
        localStorage.setItem('token', data.token);
        setProfileMenuOpen(false);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error selecting user:', error);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setAppAuthenticated(false);
    setProfileMenuOpen(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  if (!appAuthenticated) {
    return <AppPassword onAppLogin={handleAppLogin} />;
  }

  if (!user) {
    return (
      <UserSelection
        users={availableUsers}
        loading={profilesLoading}
        error={profilesError}
        onUserSelect={handleUserSelect}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
                >
                  <Menu className="h-6 w-6" />
                </button>
                <Shield className="h-8 w-8 text-blue-600 dark:text-blue-400 ml-4 lg:ml-0" />
                <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">Portal Seguros</span>
              </div>
              <div className="flex items-center space-x-4">
                <ThemeToggle />
                {/* Profile Switching Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="flex items-center space-x-2 p-2 rounded-md text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  >
                    <User className="h-5 w-5" />
                    <span className="text-sm font-medium">{user.name}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  
                  {profileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                          Trocar Perfil
                        </div>
                        {availableUsers.map((profileUser) => (
                          <button
                            key={profileUser.id}
                            onClick={() => handleUserSelect(profileUser)}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                              user.id === profileUser.id ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <div className="flex items-center">
                              <User className="h-4 w-4 mr-2" />
                              <div>
                                <p className="font-medium">{profileUser.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{profileUser.email}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                        <div className="border-t border-gray-100 dark:border-gray-700">
                          <button
                            onClick={handleLogout}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <div className="flex items-center">
                              <LogOut className="h-4 w-4 mr-2" />
                              Sair do App
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <aside className={`${sidebarOpen ? 'block' : 'hidden'} lg:block lg:w-64 bg-white dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700`}>
            <nav className="mt-5 px-2">
              <Link
                to="/dashboard"
                className="group flex items-center px-2 py-2 text-base font-medium rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => setSidebarOpen(false)}
              >
                <LayoutDashboard className="mr-3 h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400" />
                Painel de Controlo
              </Link>
              <Link
                to="/claims"
                className="mt-1 group flex items-center px-2 py-2 text-base font-medium rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => setSidebarOpen(false)}
              >
                <FileText className="mr-3 h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400" />
                Processos
              </Link>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 p-6 overflow-auto">
            <Routes>
              <Route path="/dashboard" element={<Dashboard user={user} />} />
              <Route path="/claims" element={<Claims user={user} />} />
              <Route path="/" element={<Dashboard user={user} />} />
            </Routes>
          </main>
        </div>
      </div>
    );
  }

export default App;

const AppWithTheme = () => (
  <ThemeProvider>
    <App />
  </ThemeProvider>
);

export { AppWithTheme };




